import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X } from 'lucide-react';
import './ExpandCard.css';

/* Dépliage d'une tuile en chapitre plein écran (pattern Site CPE) :
   le panneau part du rectangle exact de la tuile cliquée, grandit
   jusqu'au plein écran, puis le contenu détaillé apparaît. */

type Phase = 'closed' | 'opening' | 'open' | 'closing';

interface ExpandCardProps {
  cardClassName: string;
  cardStyle?: React.CSSProperties;
  ariaLabelledBy?: string;
  summary: React.ReactNode;
  detail: React.ReactNode;
}

const CLOSE_MS = 500;

export default function ExpandCard({
  cardClassName, cardStyle, ariaLabelledBy, summary, detail,
}: ExpandCardProps): JSX.Element {
  const cardRef = useRef<HTMLElement>(null);
  const [phase, setPhase] = useState<Phase>('closed');
  const [rect, setRect] = useState<DOMRect | null>(null);

  function open(): void {
    if (phase !== 'closed' || cardRef.current === null) return;
    setRect(cardRef.current.getBoundingClientRect());
    setPhase('opening');
  }

  function close(): void {
    if (phase !== 'open') return;
    // On repart du rectangle actuel de la tuile (elle a pu bouger au scroll).
    if (cardRef.current !== null) setRect(cardRef.current.getBoundingClientRect());
    setPhase('closing');
  }

  useEffect(() => {
    if (phase === 'opening') {
      // Deux images d'attente : la 1re applique le rectangle de départ,
      // la 2e déclenche la transition vers le plein écran.
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => { setPhase('open'); });
      });
      return () => { cancelAnimationFrame(id); };
    }
    if (phase === 'closing') {
      const id = window.setTimeout(() => { setPhase('closed'); }, CLOSE_MS);
      return () => { clearTimeout(id); };
    }
    return undefined;
  }, [phase]);

  useEffect(() => {
    if (phase === 'closed') return undefined;
    // Le verrou retire la scrollbar : on réserve sa largeur pour que
    // la page (et la tuile d'origine) ne se décale pas d'un pixel.
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${String(scrollbarW)}px`;
    // Les éléments fixed (navbar) ignorent le padding du body : ils lisent
    // cette variable pour se décaler d'autant.
    document.body.style.setProperty('--scrollbar-comp', `${String(scrollbarW)}px`);
    document.body.classList.add('ExpandCard-lock');
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('ExpandCard-lock');
      document.body.style.paddingRight = '';
      document.body.style.removeProperty('--scrollbar-comp');
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  function onPanelClick(): void {
    // Ne pas refermer si le visiteur vient de sélectionner du texte.
    const sel = window.getSelection();
    if (sel !== null && sel.toString().length > 0) return;
    close();
  }

  const expanded = phase === 'open';
  const zoomStyle: React.CSSProperties | undefined =
    expanded || rect === null
      ? undefined
      : { top: rect.top, left: rect.left, width: rect.width, height: rect.height };

  return (
    <>
      <article
        ref={cardRef}
        className={`${cardClassName} ExpandCard__tile${phase !== 'closed' ? ' ExpandCard__tile--hidden' : ''}`}
        style={cardStyle}
        aria-labelledby={ariaLabelledBy}
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
        }}
      >
        {summary}
        <span className="ExpandCard__hint" aria-hidden="true">
          <Plus size={15} strokeWidth={2} />
        </span>
        {/* Contenu complet gardé dans le DOM pour le pré-rendu react-snap / SEO. */}
        {phase === 'closed' && <div hidden>{detail}</div>}
      </article>

      {phase !== 'closed' && createPortal(
        <div
          className={`ExpandCard__zoom${expanded ? ' ExpandCard__zoom--open' : ''} ${cardClassName}`}
          style={zoomStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledBy}
          onClick={onPanelClick}
        >
          <div className={`ExpandCard__detail${expanded ? ' ExpandCard__detail--visible' : ''}`}>
            {detail}
          </div>
          <button
            type="button"
            className={`ExpandCard__close${expanded ? ' ExpandCard__close--visible' : ''}`}
            aria-label="Fermer"
            onClick={close}
          >
            <X size={22} strokeWidth={2} />
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}
